import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiDirectChannelsDeviceBehaviorPlugin } from '@shure/device-ui/direct-channels/data-access';
import { DeviceUiInputsDeviceBehaviorPlugin } from '@shure/device-ui/inputs/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin, IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiOutputsDeviceBehaviorPlugin } from '@shure/device-ui/outputs/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannel,
	SchematicsDeviceChannelDspKind
} from '@shure/device-ui/schematics/data-access';
import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspChannelType, DspCopyChannelProperty, DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { doesAccessPointSupportFusion } from '@shure/shared/angular/data-access/system-api/mappers';
import {
	ChannelGroup,
	DeviceModel,
	ChannelAnalogLevel,
	DeviceConferenceOperationMode
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class MxwApxD2DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiInputsDeviceBehaviorPlugin,
		DeviceUiOutputsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiDirectChannelsDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.MXWAPXD2];

	constructor(protected readonly transloco: TranslocoService) {}

	public getInputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DanteInput,
			ChannelGroup.Generic,
			ChannelGroup.DanteAuxInput,
			ChannelGroup.AnalogInput,
			ChannelGroup.UsbInput
		];
	}

	public getFusionEnabledInputChannelGroups(model: DeviceModel): ChannelGroup[] {
		return [
			...this.getInputChannelGroups(model).slice(0, 1),
			ChannelGroup.DanteMicInput,
			...this.getInputChannelGroups(model).slice(1)
		];
	}

	// put it in the order you want to display, will use the index to sort it
	public getDirectChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DirectOutputDanteMicInput,
			ChannelGroup.DirectOutput,
			ChannelGroup.DirectOutputAnalogInput
		];
	}

	public getFusionEnabledDirectChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DirectOutputDanteMicInput,
			ChannelGroup.DirectOutput,
			ChannelGroup.DirectOutputAnalogInput
		];
	}

	public getOutputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DanteOutput,
			ChannelGroup.DanteLineOutput,
			ChannelGroup.AnalogOutput,
			ChannelGroup.UsbOutput,
			ChannelGroup.Back,
			ChannelGroup.AuracastOutput
		];
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return true;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.automixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.direct-channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(
				([
					schematicLabel,
					inputsLabel,
					automixerLabel,
					directChannelsLabel,
					matrixMixerLabel,
					outputsLabel,
					presetsLabel,
					settingsLabel
				]) => [
					{
						route: DeviceUiRoute.Schematic,
						label: schematicLabel
					},
					{
						route: DeviceUiRoute.Inputs,
						label: inputsLabel
					},
					{
						route: DeviceUiRoute.Intellimix,
						label: automixerLabel
					},
					{
						route: DeviceUiRoute.DirectChannels,
						label: directChannelsLabel
					},
					{
						route: DeviceUiRoute.Matrixmixer,
						label: matrixMixerLabel
					},
					{
						route: DeviceUiRoute.Outputs,
						label: outputsLabel
					},
					{
						route: DeviceUiRoute.Presets,
						label: presetsLabel
					},
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					}
				]
			)
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedLegacy;
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	// put it in the order you want to display, will use the index to sort it
	public getAutomixedChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteMicInput, ChannelGroup.Generic, ChannelGroup.AnalogInput];
	}

	public getInputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Agc, 2]
		]);
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(): boolean {
		return true;
	}

	public hideAgcMetering(_model: DeviceModel): boolean {
		return false;
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteInput, 0],
			[ChannelGroup.Generic, 1],
			[ChannelGroup.AnalogInput, 2],
			[ChannelGroup.DirectOutput, 3],
			[ChannelGroup.DirectOutputAnalogInput, 4],
			[ChannelGroup.Automix, 5],
			[ChannelGroup.UsbInput, 6]
		]);
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([['equalizer', 1]]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		_channelType: DspChannelType,
		_selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		return ['muted', 'gain', 'analogLevel'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getOutputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([[DspType.Peq, 1]]);
	}

	public getNumericDbValueForAnalogOutputLevel(
		_model: DeviceModel,
		analogLevel: ChannelAnalogLevel
	): string | undefined {
		switch (analogLevel) {
			case ChannelAnalogLevel.Aux:
				return '-10 dBu';
			case ChannelAnalogLevel.Line:
				return '+4 dBu';
			case ChannelAnalogLevel.Mic:
				return '-30 dBu';
			default:
				return undefined;
		}
	}

	public getDeviceOutputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteOutput, 1],
			[ChannelGroup.AnalogOutput, 2],
			[ChannelGroup.UsbOutput, 3],
			[ChannelGroup.Back, 4],
			[ChannelGroup.AuracastOutput, 5],
			[ChannelGroup.AecReference, 6]
		]);
	}

	public connectSchematicsAutomixerToSecondChannelGroup(_model: DeviceModel): boolean {
		return true;
	}

	public getAnalogLevelConstraintsOrdering(_model: DeviceModel): Map<ChannelAnalogLevel, number> | undefined {
		return new Map<ChannelAnalogLevel, number>([
			[ChannelAnalogLevel.Line, 1],
			[ChannelAnalogLevel.Aux, 2],
			[ChannelAnalogLevel.Mic, 3],
			[ChannelAnalogLevel.Headphone, 4]
		]);
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}

	public isFusionEnabled(
		_model: DeviceModel,
		mode: DeviceConferenceOperationMode,
		firmwareVersion: string,
		virtual: boolean
	): boolean {
		return (
			[
				DeviceConferenceOperationMode.Conference,
				DeviceConferenceOperationMode.Presentation,
				DeviceConferenceOperationMode.Custom
			].includes(mode) && doesAccessPointSupportFusion(firmwareVersion, virtual)
		);
	}

	public getFilteredInputChannelGroups(
		model: DeviceModel,
		mode: DeviceConferenceOperationMode,
		firmwareVersion: string,
		virtual: boolean
	): ChannelGroup[] {
		return this.isFusionEnabled(model, mode, firmwareVersion, virtual) ? [] : [ChannelGroup.DanteMicInput];
	}

	public isAccessPoint(): boolean {
		return true;
	}
}
